import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@assets/icons/search.svg';
import EmptyStateIcon from '@assets/icons/table/results-empty-state.svg';
import { BaseButton, BaseCheckbox } from '@components';
import { BaseInput } from '@components';
import { useLingui } from '@lingui/react/macro';
import type {
    CommonMultiSearchSelectOption,
    CommonMultiSearchSelectProps,
    CommonMultiSearchSelectValue,
} from '@routes/sidebar/admin-center/___shared___/components/multi-search-select/types.ts';
import { StyledSelect } from '@routes/sidebar/admin-center/___shared___/components/tables/regular/styled.ts';
import {
    ButtonsWrapper,
    CheckboxLabelWrapper,
    CustomCategoryFilterWrapper as Wrapper,
    SelectedCountSectionVessels,
    SubItemCategoryLabel,
    SubItemsSection,
    SubitemWrapper,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/styled.tsx';
import { mapArrToStrings } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/table/utils.ts';
import { Form } from 'antd';
import debounce from 'lodash/debounce';

import { EmptyStateText, EmptyStateWrapper } from './styled.ts';

const COMMON_SELECT_INITIAL_PAGE_SIZE = 10;
const COMMON_SELECT_PAGE_SIZE_STEP = 10;
const COMMON_SELECT_SEARCH_MIN_LENGTH = 3;
const COMMON_SELECT_SEARCH_DEBOUNCE_MS = 300;

export const CommonMultiSearchSelect: React.FC<CommonMultiSearchSelectProps> = ({
    name,
    rules,
    placeholder,
    disabled,
    loading,
    options,
    onChange,
    onLoadOptions,
    selectProps,
    allowClear = true,
    showSearch = true,
    searchPlaceholder = 'Search...',
    hasMore = false,
}) => {
    const { t } = useLingui();
    const form = Form.useFormInstance();
    const selectedValues = Form.useWatch(name, form);

    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [pageSize, setPageSize] = useState(COMMON_SELECT_INITIAL_PAGE_SIZE);
    const [draftSelectedValues, setDraftSelectedValues] = useState<CommonMultiSearchSelectValue[]>([]);

    const normalizedSelectedValues: CommonMultiSearchSelectValue[] = Array.isArray(selectedValues)
        ? selectedValues
        : [];

    const normalizedOptions: CommonMultiSearchSelectOption[] = options.map((option) => ({
        value: option.value,
        label: String(option.label),
        subLabel: String(option.subLabel ?? ''),
        disabled: Boolean((option as any).disabled),
    }));

    const selectedOptions = normalizedSelectedValues.map((value) => {
        const selectedOption = normalizedOptions.find((option) => String(option.value) === String(value));

        return {
            value,
            label: selectedOption?.label ?? String(value),
        };
    });

    const searchQuery = searchValue.trim().toLowerCase();

    const filteredOptions = searchQuery
        ? normalizedOptions.filter(
              (option) =>
                  option.label.toLowerCase().includes(searchQuery) ||
                  option.subLabel.toLowerCase().includes(searchQuery),
          )
        : normalizedOptions;

    const debouncedLoadOptions = useMemo(
        () =>
            debounce((textSearch: string, nextPageSize: number) => {
                onLoadOptions?.(textSearch, nextPageSize);
            }, COMMON_SELECT_SEARCH_DEBOUNCE_MS),
        [onLoadOptions],
    );

    useEffect(
        () => () => {
            debouncedLoadOptions.cancel();
        },
        [debouncedLoadOptions],
    );

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            setDraftSelectedValues(normalizedSelectedValues);
            setPageSize(COMMON_SELECT_INITIAL_PAGE_SIZE);
            onLoadOptions?.('', COMMON_SELECT_INITIAL_PAGE_SIZE);
        } else {
            debouncedLoadOptions.cancel();
        }

        setSearchValue('');
        setOpen(nextOpen);
    };

    const handleClose = () => {
        setSearchValue('');
        debouncedLoadOptions.cancel();
        setOpen(false);
    };

    const handleSearch = (value: string) => {
        const previousSearchValue = searchValue.trim();
        const nextSearchValue = value.trim();

        setSearchValue(value);

        if (previousSearchValue === nextSearchValue) return;

        if (normalizedOptions.length === 0) return;

        setPageSize(COMMON_SELECT_INITIAL_PAGE_SIZE);

        if (nextSearchValue.length === 0) {
            debouncedLoadOptions.cancel();
            onLoadOptions?.('', COMMON_SELECT_INITIAL_PAGE_SIZE);

            return;
        }

        if (nextSearchValue.length < COMMON_SELECT_SEARCH_MIN_LENGTH) {
            debouncedLoadOptions.cancel();

            return;
        }

        debouncedLoadOptions(nextSearchValue, COMMON_SELECT_INITIAL_PAGE_SIZE);
    };

    const handleLoadMore = () => {
        const nextPageSize = pageSize + COMMON_SELECT_PAGE_SIZE_STEP;

        setPageSize(nextPageSize);
        onLoadOptions?.(searchValue.trim(), nextPageSize);
    };

    const handleCheckboxChange = (value: CommonMultiSearchSelectValue, checked: boolean) => {
        setDraftSelectedValues((prev) => {
            if (checked) {
                return prev.some((selectedValue) => String(selectedValue) === String(value)) ? prev : [...prev, value];
            }

            return prev.filter((selectedValue) => String(selectedValue) !== String(value));
        });
    };

    const handleSelectAll = () => {
        setDraftSelectedValues(filteredOptions.filter((option) => !option.disabled).map((option) => option.value));
    };

    const handleClearAll = () => {
        setDraftSelectedValues([]);
    };

    const handleClear = () => {
        debouncedLoadOptions.cancel();
        setSearchValue('');
        setPageSize(COMMON_SELECT_INITIAL_PAGE_SIZE);
        setDraftSelectedValues([]);
        onLoadOptions?.('', COMMON_SELECT_INITIAL_PAGE_SIZE);
    };

    const handleCancel = () => {
        setDraftSelectedValues(normalizedSelectedValues);
        handleClose();
    };

    const handleDone = () => {
        form.setFieldValue(name, draftSelectedValues);
        onChange?.(mapArrToStrings(draftSelectedValues));
        handleClose();
    };

    const handleSelectChange = (values: CommonMultiSearchSelectValue[]) => {
        const nextValues = values ?? [];

        form.setFieldValue(name, nextValues);
        onChange?.(mapArrToStrings(nextValues));
    };

    return (
        <Form.Item name={name} rules={rules} style={{ margin: 0 }}>
            <StyledSelect
                {...selectProps}
                mode="multiple"
                open={open}
                value={normalizedSelectedValues}
                options={selectedOptions}
                optionLabelProp="label"
                placeholder={placeholder}
                disabled={disabled}
                loading={loading}
                allowClear={allowClear}
                showSearch={false} // here is false because we are using custom input search
                onOpenChange={handleOpenChange}
                onChange={handleSelectChange}
                popupRender={() => (
                    <Wrapper $isFixedWidth={false} onMouseDown={(event) => event.preventDefault()}>
                        <ButtonsWrapper>
                            <BaseButton $customType="outline" onClick={handleSelectAll}>
                                {t`Select All`}
                            </BaseButton>
                            <BaseButton $customType="outline" onClick={handleClearAll}>
                                {t`Clear All`}
                            </BaseButton>
                        </ButtonsWrapper>

                        {!!showSearch && (
                            <BaseInput
                                allowClear
                                value={searchValue}
                                placeholder={searchPlaceholder}
                                prefixIcon={<SearchIcon />}
                                onChange={(event) => handleSearch(event.target.value)}
                                onClear={handleClear}
                            />
                        )}

                        {filteredOptions.length > 0 ? (
                            <SubItemsSection>
                                {filteredOptions.map((option) => (
                                    <SubitemWrapper key={option.value}>
                                        <CheckboxLabelWrapper>
                                            <BaseCheckbox
                                                checked={draftSelectedValues.some(
                                                    (selectedValue) => String(selectedValue) === String(option.value),
                                                )}
                                                disabled={option.disabled}
                                                onChange={(event) =>
                                                    handleCheckboxChange(option.value, event.target.checked)
                                                }
                                            >
                                                {option.label}
                                            </BaseCheckbox>
                                        </CheckboxLabelWrapper>

                                        {!!option.subLabel && (
                                            <SubItemCategoryLabel>{option.subLabel}</SubItemCategoryLabel>
                                        )}
                                    </SubitemWrapper>
                                ))}
                            </SubItemsSection>
                        ) : (
                            <EmptyStateWrapper>
                                <EmptyStateIcon />
                                <EmptyStateText>{t`No items match your search`}</EmptyStateText>
                            </EmptyStateWrapper>
                        )}

                        {!!hasMore && (
                            <ButtonsWrapper>
                                <BaseButton $customType="outline" disabled={loading} onClick={handleLoadMore}>
                                    {t`Load more`}
                                </BaseButton>
                            </ButtonsWrapper>
                        )}

                        <SelectedCountSectionVessels>
                            {draftSelectedValues.length} of {normalizedOptions.length} {t`selected`}
                        </SelectedCountSectionVessels>

                        <ButtonsWrapper>
                            <BaseButton $customType="outline" onClick={handleCancel}>
                                {t`Cancel`}
                            </BaseButton>
                            <BaseButton onClick={handleDone}>{t`Done`}</BaseButton>
                        </ButtonsWrapper>
                    </Wrapper>
                )}
            />
        </Form.Item>
    );
};
